import React from 'react';

function SettingsModal({
  isOpen,
  onClose,
  authUser,
  paidRetentionEnabled,
  onTogglePaidRetention,
  collapsedStat,
  onSetCollapsedStat,
  autoRotateStats,
  onToggleAutoRotate,
  buddy,
  onRerollBuddy,
  theme,
  onSetTheme,
  colorScheme,
  onSetColorScheme,
  reduceMotion,
  onToggleReduceMotion,
  enableBackToTop,
  onToggleBackToTop,
  devUnlocked,
  devMode,
  onDevUnlockClick,
  onToggleDevMode,
  onOpenDevPanel,
  version,
  buildId,
}) {
  if (!isOpen) return null;

  return (
    <div className="settings-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(event) => event.stopPropagation()}>
        <div className="settings-header">
          <h2>Settings</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>
        {authUser?.email && (
          <div className="settings-section">
            <h3>Account</h3>
            <p className="settings-note">Account: {authUser.email}</p>
            <div className="settings-toggle">
              <span>Paid retention (unlimited Fit history)</span>
              <button
                type="button"
                className={`toggle-pill ${paidRetentionEnabled ? 'on' : ''}`}
                onClick={onTogglePaidRetention}
                aria-pressed={paidRetentionEnabled}
              >
                <span className="toggle-indicator" />
              </button>
            </div>
            <p className="settings-note">
              {paidRetentionEnabled
                ? 'Retention mode: Paid (indefinite detailed history).'
                : 'Retention mode: Free (12-month detailed history + monthly rollups).'}
            </p>
          </div>
        )}
        <div className="settings-section">
          <h3>Progress Banner Display</h3>
          <div className="settings-options">
            {['streak', 'xp', 'level'].map((option) => (
              <button
                key={option}
                type="button"
                className={`settings-option ${collapsedStat === option ? 'active' : ''}`}
                onClick={() => onSetCollapsedStat(option)}
              >
                {option === 'streak' && 'Streak'}
                {option === 'xp' && 'XP'}
                {option === 'level' && 'Level'}
              </button>
            ))}
          </div>
          <div className="settings-toggle">
            <span>Auto-rotate stats</span>
            <button
              type="button"
              className={`toggle-pill ${autoRotateStats ? 'on' : ''}`}
              onClick={onToggleAutoRotate}
              aria-pressed={autoRotateStats}
            >
              <span className="toggle-indicator" />
            </button>
          </div>
          <p className="settings-note">Default is Streak. If Streak is 0, XP will display.</p>
        </div>

        <div className="settings-section">
          <h3>Buddy Customization</h3>
          <div className="buddy-info-display">
            <div className="buddy-info-row">
              <span className="buddy-info-label">Animal:</span>
              <span className="buddy-info-value">{buddy?.getAnimalConfig().name}</span>
            </div>
            <div className="buddy-info-row">
              <span className="buddy-info-label">Egg Color:</span>
              <div className="buddy-color-row">
                <div
                  className="buddy-color-preview"
                  style={{ backgroundColor: buddy?.getEggColorConfig().hex }}
                />
                <span className="buddy-info-value">{buddy?.getEggColorConfig().name}</span>
              </div>
            </div>
            <div className="buddy-info-row">
              <span className="buddy-info-label">Rarity:</span>
              <span className={`buddy-rarity-display ${buddy?.rarity}`}>
                {buddy?.rarity === 'mythical' && '⭐ Mythical'}
                {buddy?.rarity === 'rare' && '💎 Rare'}
                {buddy?.rarity === 'common' && 'Common'}
              </span>
            </div>
          </div>
          <button
            type="button"
            className="lucky-reroll-btn"
            onClick={onRerollBuddy}
          >
            🎲 Try Your Luck Again
          </button>
          <p className="settings-note">Reroll gives you a chance at rare or mythical animals, but resets your buddy to level 1.</p>
        </div>

        <div className="settings-section">
          <h3>Appearance</h3>
          <div className="settings-options">
            <button
              type="button"
              className={`settings-option ${theme === 'light' ? 'active' : ''}`}
              onClick={() => onSetTheme('light')}
            >
              Light
            </button>
            <button
              type="button"
              className={`settings-option ${theme === 'dark' ? 'active' : ''}`}
              onClick={() => onSetTheme('dark')}
            >
              Dark
            </button>
          </div>
          <div className="settings-subtitle">Color scheme</div>
          <div className="settings-options">
            <button type="button" className={`settings-option ${colorScheme === 'default' ? 'active' : ''}`} onClick={() => onSetColorScheme('default')}>Default</button>
            <button type="button" className={`settings-option ${colorScheme === 'pastel' ? 'active' : ''}`} onClick={() => onSetColorScheme('pastel')}>Pastel</button>
            <button type="button" className={`settings-option ${colorScheme === 'earth' ? 'active' : ''}`} onClick={() => onSetColorScheme('earth')}>Earth</button>
            <button type="button" className={`settings-option ${colorScheme === 'heavy' ? 'active' : ''}`} onClick={() => onSetColorScheme('heavy')}>Heavy</button>
            <button type="button" className={`settings-option ${colorScheme === 'heavy-light' ? 'active' : ''}`} onClick={() => onSetColorScheme('heavy-light')}>Heavy Light</button>
          </div>
          <div className="settings-toggle">
            <span>Reduce motion</span>
            <button
              type="button"
              className={`toggle-pill ${reduceMotion ? 'on' : ''}`}
              onClick={onToggleReduceMotion}
              aria-pressed={reduceMotion}
            >
              <span className="toggle-indicator" />
            </button>
          </div>
          <div className="settings-toggle">
            <span>Show Back to Top</span>
            <button
              type="button"
              className={`toggle-pill ${enableBackToTop ? 'on' : ''}`}
              onClick={onToggleBackToTop}
              aria-pressed={enableBackToTop}
            >
              <span className="toggle-indicator" />
            </button>
          </div>
        </div>
        <div className="settings-section">
          <div className="dev-section-header">
            <button
              type="button"
              className="dev-unlock-trigger"
              onClick={onDevUnlockClick}
              title="Developer Mode"
            >
              Developer Mode
            </button>
          </div>
          {devUnlocked ? (
            <>
              <div className="settings-toggle">
                <span>Enable DEV mode</span>
                <button
                  type="button"
                  className={`toggle-pill ${devMode ? 'on' : ''}`}
                  onClick={onToggleDevMode}
                  aria-pressed={devMode}
                >
                  <span className="toggle-indicator" />
                </button>
              </div>
              <button
                type="button"
                className="dev-panel-btn"
                onClick={onOpenDevPanel}
                disabled={!devMode}
              >
                Open DEV Panel
              </button>
              <p className="settings-note">DEV tools for testing buddies, eggs, and progression.</p>
            </>
          ) : null}
        </div>
        <div className="settings-section">
          <h3>Quick Links</h3>
          <div className="settings-links">
            <a href="/privacy.html" target="_blank" rel="noreferrer">Privacy Policy</a>
            <a href="/terms.html" target="_blank" rel="noreferrer">Terms of Service</a>
            <span className="settings-version">Ver. {version} • Build {buildId}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SettingsModal;
